import {
  AfterContentChecked,
  AfterContentInit,
  AfterViewChecked,
  AfterViewInit,
  DoCheck,
  OnChanges,
  OnDestroy,
  OnInit,
  ɵDirectiveDef as DirectiveDef,
  ɵNG_COMP_DEF as NG_COMP_DEF,
  ɵNG_DIR_DEF as NG_DIR_DEF,
} from '@angular/core';

import { Subject } from 'rxjs';

type Hooks =
  | 'onChanges'
  | 'onInit'
  | 'doCheck'
  | 'afterContentInit'
  | 'afterContentChecked'
  | 'afterViewInit'
  | 'afterViewChecked'
  | 'onDestroy';

type ComponentPrototype = Partial<
  OnChanges &
    OnInit &
    DoCheck &
    AfterContentInit &
    AfterContentChecked &
    AfterViewInit &
    AfterViewChecked &
    OnDestroy
>;

const hookEmitOnce: Record<Hooks, boolean> = {
  onChanges: false,
  onInit: true,
  doCheck: false,
  afterContentInit: true,
  afterContentChecked: false,
  afterViewInit: true,
  afterViewChecked: false,
  onDestroy: true,
};

enum NgHookName {
  onChanges = 'ngOnChanges',
  onInit = 'ngOnInit',
  doCheck = 'ngDoCheck',
  afterContentInit = 'ngAfterContentInit',
  afterContentChecked = 'ngAfterContentChecked',
  afterViewInit = 'ngAfterViewInit',
  afterViewChecked = 'ngAfterViewChecked',
  onDestroy = 'ngOnDestroy',
}

const NG_FACTORY_DEF = 'ɵfac';
const LIFECYCLE_REGISTRY = Symbol('__lifecycle_registry__');
const IS_PROTOTYPE_PATCHED = Symbol('is prototype patched');
const DESTROY_KEY = Symbol('__ngOnDestroy$__');

const warnDuplicateLifecycle = (hookName: Hooks, target: any) => {
  const angularHookName = NgHookName[hookName];
  const componentName = target.constructor.name;
  console.error(
    `Don't use observable lifecycle hooks with default lifecycle hooks, you created '${hookName}' observable lifecycle but '${angularHookName}' exist in component ${componentName}`,
  );
};

const createDestroyHook = (target: any) => {
  const proto: ComponentPrototype = target.constructor.prototype;
  const onDestroy = proto.ngOnDestroy;
  // @ts-ignore
  proto.ngOnDestroy = function (this: Record<string, Subject<void>>) {
    const registry = <Array<[Hooks, string]>>Object.entries(target[LIFECYCLE_REGISTRY]);
    registry
      .filter(([lifecycleName]) => !hookEmitOnce[lifecycleName])
      .forEach(([_, propKey]) => this[propKey].complete());

    const onDestroyPropName = target[LIFECYCLE_REGISTRY].onDestroy;
    this[onDestroyPropName].next();
    this[onDestroyPropName].complete();

    if (onDestroy) onDestroy.call(this);
  };
};

const createObservableHook = (target: any, propertyKey: string, name: Hooks) => {
  const proto: ComponentPrototype = target.constructor.prototype;
  const hookAutoComplete = hookEmitOnce[name];
  const existingHookFn = proto[NgHookName[name]];

  if (!target[LIFECYCLE_REGISTRY]) {
    target[LIFECYCLE_REGISTRY] = {};
  }

  if (name !== 'onDestroy') {
    if (existingHookFn) {
      warnDuplicateLifecycle(name, target);
    }

    proto[NgHookName[name]] = function (this: Record<string, Subject<void>>, ...args: any[]) {
      this[propertyKey].next(...args);
      if (hookAutoComplete) {
        this[propertyKey].complete();
      }

      if (existingHookFn) existingHookFn.apply(this, arguments);
    };
  }

  if ((!hookAutoComplete || name === 'onDestroy') && !target[LIFECYCLE_REGISTRY].onDestroy) {
    target[LIFECYCLE_REGISTRY].onDestroy = DESTROY_KEY;
    createDestroyHook(target);
  }

  target[LIFECYCLE_REGISTRY][name] = propertyKey;
};

// eslint-disable-next-line max-lines-per-function
export const LifeCycle = (lifeCycleName: Hooks): PropertyDecorator =>
  // eslint-disable-next-line max-lines-per-function
  function (target: any, propertyKey: string) {
    const isJIT = !target.constructor[NG_COMP_DEF] || !target.constructor[NG_DIR_DEF];

    const patchClass = () => {
      const dirDef: DirectiveDef<any> =
        target.constructor[NG_COMP_DEF] || target.constructor[NG_DIR_DEF];
      if (!dirDef) {
        console.warn(target, 'is not patched for life cycle hooks');
        return;
      }
      const originalFactory = dirDef.factory || target.constructor[NG_FACTORY_DEF];

      if (!target[LIFECYCLE_REGISTRY]) {
        // @ts-ignore
        dirDef['factory'] = function () {
          const newFactory = dirDef.type;
          if (!newFactory.prototype[IS_PROTOTYPE_PATCHED]) {
            for (const propName of Object.values(target[LIFECYCLE_REGISTRY])) {
              Object.defineProperty(newFactory.prototype, <string>propName, {
                get() {
                  if (this.hasOwnProperty(propName)) return this[<string>propName];

                  const subject = new Subject<void>();
                  Object.defineProperty(this, <string>propName, {
                    value: subject,
                    configurable: false,
                    writable: false,
                  });
                  return subject;
                },
              });
            }
            newFactory.prototype[IS_PROTOTYPE_PATCHED] = true;
          }

          return originalFactory(newFactory);
        };
      }

      createObservableHook(target, propertyKey, lifeCycleName);
    };

    isJIT ? Promise.resolve().then(patchClass) : patchClass();
  };
